import React, { useState, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import { Country, State, City } from 'country-state-city';
import ModalHeading from '../../components/shared/modal-heading';
import { updateVendor } from '../../services/vendors-service';

const EditModal = ({ selectedRow, setSelectedRow, setIsEditModalOpen, setData, fetchVendors, accountId }) => {
    const [countryOptions, setCountryOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [loadingFields, setLoadingFields] = useState({});
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });

    // console.log(selectedRow)

    // Load countries on component mount
    useEffect(() => {
        const countries = Country.getAllCountries().map((country) => ({
            value: country.name, // Store country name instead of isoCode
            label: country.name,
            isoCode: country.isoCode // Keep isoCode for state lookup
        }));
        setCountryOptions([{ value: '', label: 'Select Country' }, ...countries]);
    }, []); 

    // Load states when country changes
    useEffect(() => {
        if (selectedRow?.country) {
            const selectedCountry = countryOptions.find(c => c.value === selectedRow.country);
            if (selectedCountry) {
                const states = State.getStatesOfCountry(selectedCountry.isoCode).map((state) => ({
                    value: state.name, // Store state name instead of isoCode
                    label: state.name,
                    isoCode: state.isoCode // Keep isoCode for city lookup
                }));
                setStateOptions([{ value: '', label: 'Select State' }, ...states]);
            }
        } else {
            setStateOptions([{ value: '', label: 'Select State' }]);
        }
    }, [selectedRow?.country, countryOptions]);

    // Load cities when state changes
    useEffect(() => {
        if (selectedRow?.country && selectedRow?.state) {
            const selectedCountry = countryOptions.find(c => c.value === selectedRow.country);
            const selectedState = stateOptions.find(s => s.value === selectedRow.state);

            if (selectedCountry && selectedState) {
                const cities = City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode).map((city) => ({
                    value: city.name,
                    label: city.name,
                }));
                setCityOptions([{ value: '', label: 'Select City' }, ...cities]);
            }
        } else {
            setCityOptions([{ value: '', label: 'Select City' }]);
        }
    }, [selectedRow?.country, selectedRow?.state, countryOptions, stateOptions]);

    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Handle text field updates
    const handleFieldUpdate = async (value, fieldName) => {
        console.log(value, fieldName)
        if (!selectedRow) return;

        setLoadingFields((prev) => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        try {
            const payload = { [fieldName]: value };
            const response = await updateVendor(accountId, selectedRow.vendorId, payload);

            if (response.success) {
                const updatedRow = { ...selectedRow, [fieldName]: value };
                setSelectedRow(updatedRow);
                setData((prevData) =>
                    prevData.map((row) =>
                        row.vendorId === selectedRow.vendorId ? updatedRow : row
                    )
                );
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success'
                });
            } else {
                setUpdateStatus({
                    message: response.data?.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error',
                });
            }
        } catch (error) {
            console.error('Error updating vendor:', error);
            setUpdateStatus({
                message: `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error',
            });
        } finally {
            setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
        }
    };

    // Handle select changes
    const handleSelectChange = async (option, fieldName) => {
        console.log(option, fieldName)
        if (!selectedRow) return;

        setLoadingFields((prev) => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const newValue = option ? option : '';
        let payload = { [fieldName]: newValue };

        // Handle dependent fields
        if (fieldName === 'country') {
            payload = { ...payload, state: '', city: '' };
        } else if (fieldName === 'state') {
            payload = { ...payload, city: '' };
        }


        try {
            console.log("Data : ",payload)
            const response = await updateVendor(accountId, selectedRow.vendorId, payload);
            console.log(response)
            if (response.success) {
                const updatedRow = {
                    ...selectedRow,
                    ...payload
                };
                setSelectedRow(updatedRow);
                setData((prevData) =>
                    prevData.map((row) =>
                        row.vendorId === selectedRow.vendorId ? updatedRow : row
                    )
                );
                setUpdateStatus({
                    message: `${labelMap[fieldName] || fieldName} updated successfully!`,
                    type: 'success'
                });
            } else {
                setUpdateStatus({
                    message: response.data?.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error',
                });
            }
        } catch (error) {
            console.error('Error updating vendor:', error);
            setUpdateStatus({
                message: `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error',
            });
        } finally {
            setLoadingFields((prev) => ({ ...prev, [fieldName]: false }));
        }
    };

    const labelMap = {
        vendorId: 'Vendor ID',
        name: 'Name',
        country: 'Country',
        state: 'State',
        city: 'City',
        address: 'Address',
        postalCode: 'Postal Code',
        status: 'Status',
        category: 'Category',
    };

    // Category options
    const categoryOptions = [
        { value: '', label: 'Select Category' },
        { value: 'Retail', label: 'Retail' },
        { value: 'Service', label: 'Service' },
        { value: 'Manufacturing', label: 'Manufacturing' },
    ];

    // Status options
    const statusOptions = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'pending', label: 'Pending' },
        { value: 'suspended', label: 'Suspended' },
    ];

    if (!selectedRow) return null;

    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Vendor Information</ModalHeading>

            <button
                className="hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full"
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className="text-2xl" />
            </button>

            {updateStatus.message && (
                <div
                    className={`px-4 py-2 text-sm ${updateStatus.type === 'success'
                        ? 'text-green-600 bg-green-100'
                        : updateStatus.type === 'error'
                            ? 'text-red-600 bg-red-100'
                            : 'text-blue-600 bg-blue-100'
                        } rounded-md mx-4`}
                >
                    {updateStatus.message}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">


                <EditableField
                    label="Name"
                    name="name"
                    value={selectedRow.name || ''}
                    onChange={(value) => handleFieldUpdate(value, 'name')}
                    type={'text'}
                    isLoading={loadingFields.name}
                    required
                />

                <EditableField
                    label="Category"
                    name="category"
                    value={selectedRow.category || ''}
                    onChange={(option) => handleSelectChange(option, 'category')}
                    type="select"
                    options={categoryOptions}
                    isLoading={loadingFields.category}
                />

                <EditableField
                    label="Address"
                    name="address"
                    value={selectedRow.address || ''}
                    onChange={(value) => handleFieldUpdate(value, 'address')}
                    type="text"
                    isLoading={loadingFields.address}
                />

                <EditableField
                    label="Country"
                    name="country"
                    value={selectedRow.country || ''}
                    onChange={(option) => handleSelectChange(option, 'country')}
                    type="select"
                    options={countryOptions}
                    isLoading={loadingFields.country}
                />

                <EditableField
                    label="State"
                    name="state"
                    value={selectedRow.state || ''}
                    onChange={(option) => handleSelectChange(option, 'state')}
                    type="select"
                    options={stateOptions}
                    isDisabled={!selectedRow.country}
                    isLoading={loadingFields.state}
                />

                <EditableField
                    label="City"
                    name="city"
                    value={selectedRow.city || ''}
                    onChange={(option) => handleSelectChange(option, 'city')}
                    type="select"
                    options={cityOptions}
                    isDisabled={!selectedRow.state}
                    isLoading={loadingFields.city}
                />



                <EditableField
                    label="Postal Code"
                    name="postalCode"
                    value={selectedRow.postalCode || ''}
                    onChange={(value) => handleFieldUpdate(value, 'postalCode')}
                    type="text"
                    isLoading={loadingFields.postalCode}
                />

                <EditableField
                    label="Status"
                    name="status"
                    value={selectedRow.status || ''}
                    onChange={(option) => handleSelectChange(option, 'status')}
                    type="select"
                    options={statusOptions}
                    isEditable={false}
                    isLoading={loadingFields.status}
                />

                <EditableField
                    label="Note"
                    name="note"
                    type='textarea'
                    rows={2}
                    value={selectedRow?.note || ''}
                    onChange={handleFieldUpdate}
                    isLoading={loadingFields.note}
                />
            </div>
        </div>
    );
};

export default EditModal;